import { NgTemplateOutlet } from '@angular/common'
import type { AfterViewInit, OnDestroy, ElementRef, TemplateRef } from '@angular/core'
import {
  afterRenderEffect,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core'
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling'
import { CustomVirtualScrollDirective } from '@awork/_shared/directives/custom-virtual-scroll-strategy/custom-virtual-scroll-strategy.directive'
import { getItemOffsets } from '@awork/_shared/directives/custom-virtual-scroll-strategy/custom-virtual-scroll-strategy'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import { UserQuery } from '@awork/features/user/state/user.query'
import { RowSwipeService } from '../../directives/row-swipe/row-swipe.service'
import { ViewPersistentService } from '../../services/view-persistent/view-persistent.service'
import { ListSkeletonComponent } from '../list-skeleton/list-skeleton.component'
import type { MobileListRow } from './mobile-list-row.directive'
import { MobileListRowDirective } from './mobile-list-row.directive'
import { MobileListItemDirective } from './mobile-list-item.directive'

export const DEFAULT_MOBILE_LIST_ROW_HEIGHT = 48

interface RenderRow<T> {
  id: string
  height: number
  kind: 'item' | 'header' | 'add' | 'footer' | 'clearance' | 'list-header'
  value?: T
  indent?: number
  auxiliary?: boolean
  highlighted?: boolean
  divider?: boolean
  dividerInset?: number
}

/** Shared mobile list surface. Callers own data, ordering, actions, and expansion. */
@Component({
  selector: 'aw-mobile-list',
  templateUrl: './mobile-list.component.html',
  styleUrl: './mobile-list.component.scss',
  host: { '[style.--mobile-list-row-height.px]': 'rowHeight()' },
  imports: [
    NgTemplateOutlet,
    ScrollingModule,
    CustomVirtualScrollDirective,
    ListSkeletonComponent,
    MobileListItemDirective
  ]
})
export class MobileListComponent<T> implements AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef)
  private readonly persistence = inject(ViewPersistentService)
  private readonly swipes = inject(RowSwipeService)
  private readonly workspaceQuery = inject(WorkspaceQuery)
  private readonly userQuery = inject(UserQuery)
  private readonly workspace = this.workspaceQuery.queryCurrentWorkspace()
  private readonly user = this.userQuery.queryCurrentUser()

  readonly rows = input.required<readonly MobileListRow<T>[]>()
  /** Default height for rows without an explicit height; also sizes footer and clearance rows. */
  readonly rowHeight = input<number>(DEFAULT_MOBILE_LIST_ROW_HEIGHT)
  readonly loading = input(false)
  readonly emptyTemplate = input<TemplateRef<unknown>>()
  /** Optional scroll content before the first data row. */
  readonly headerTemplate = input<TemplateRef<unknown>>()
  readonly headerHeight = input(60)
  private readonly headerInset = signal(0)
  protected readonly renderedHeaderHeight = computed(() => this.headerHeight() + this.headerInset())
  readonly scrollKey = input('')
  /** Search, filter, and sort identity. A change resets the list to the top. */
  readonly resetKey = input('')
  readonly hasMore = input(false)
  /** Enables automatic paging while the caller's terminal group is expanded. */
  readonly pagingEnabled = input(true)
  readonly loadingMore = input(false)
  readonly loadMoreFailed = input(false)
  readonly loadingLabel = input(q.translations.common.loading)
  readonly loadMoreError = input(q.translations.common.failedToLoadData)
  readonly loadMore = output<void>()
  readonly retryLoadMore = output<void>()

  readonly viewport = viewChild(CdkVirtualScrollViewport)
  private readonly clearance = viewChild<ElementRef<HTMLElement>>('clearance')
  protected readonly rowTemplate = contentChild<MobileListRowDirective<T>>(MobileListRowDirective)
  private readonly bottomSpace = signal(92)
  protected readonly headerRowCount = computed(() => (this.headerTemplate() ? 1 : 0))
  protected readonly renderRows = computed<RenderRow<T>[]>(() => [
    ...(this.headerTemplate()
      ? [
          {
            id: 'mobile-list:header',
            kind: 'list-header' as const,
            height: this.renderedHeaderHeight(),
            auxiliary: true
          }
        ]
      : []),
    ...this.rows().map((row, index, rows) => ({
      ...row,
      height: row.height ?? this.rowHeight(),
      kind: row.kind ?? 'item',
      divider: index > 0 && !row.auxiliary && !rows[index - 1].auxiliary,
      dividerInset: 8 + Math.max(row.indent ?? 0, rows[index - 1]?.indent ?? 0)
    })),
    ...(this.rows().length && (this.hasMore() || this.loadingMore() || this.loadMoreFailed())
      ? [{ id: 'mobile-list:footer', kind: 'footer' as const, height: this.rowHeight() }]
      : []),
    ...Array.from({ length: Math.ceil(this.bottomSpace() / this.rowHeight()) }, (_, index) => ({
      id: `mobile-list:clearance:${index}`,
      kind: 'clearance' as const,
      height: this.rowHeight()
    }))
  ])
  protected readonly itemHeights = computed(() => this.renderRows().map(row => row.height))
  private readonly itemOffsets = computed(() => getItemOffsets(this.itemHeights()))
  protected readonly translations = q.translations.common
  private readonly persistenceKey = computed(() =>
    this.scrollKey() ? JSON.stringify([this.workspace()?.id, this.user()?.id, this.scrollKey()]) : ''
  )
  private observer?: ResizeObserver
  private activeKey?: string
  private activeContext = ''
  private pendingOffset?: number
  private lastSwipeOffset = 0
  private pageRequested = false
  private requestedRowCount = -1
  private restoringOffset?: number

  constructor() {
    effect(() => {
      this.rows()
      // A data change can reuse a view for a different row before the next scroll event.
      this.closeOwnedRows()
    })
    afterRenderEffect(() => {
      this.rows()
      this.renderRows()
      this.loading()
      this.loadingMore()
      this.hasMore()
      this.pagingEnabled()
      this.loadMoreFailed()

      const key = this.persistenceKey()
      const context = this.resetKey()
      const viewport = this.viewport()

      if (!viewport) {
        return
      }

      if (key !== this.activeKey) {
        this.savePosition()
        this.activeKey = key
        this.activeContext = context
        this.restoringOffset = undefined

        const saved = key ? this.persistence.getListPosition(key) : undefined

        this.pendingOffset = saved?.context === context ? saved.offset : 0
      } else if (context !== this.activeContext) {
        this.activeContext = context
        this.pendingOffset = 0
        this.restoringOffset = undefined
        this.pageRequested = false
        this.closeOwnedRows()
      }

      viewport.checkViewportSize()
      this.restorePosition()

      if (this.loadingMore() || this.requestedRowCount !== this.rows().length) {
        this.pageRequested = false
      }

      this.requestNextPage()
    })
  }

  /**
   * Keeps scrolling and dock clearance in sync with viewport changes.
   */
  ngAfterViewInit(): void {
    const viewport = this.viewport()!

    this.syncHeaderInset()

    this.observer = new ResizeObserver(() => {
      this.syncHeaderInset()

      const height = this.clearance()?.nativeElement.getBoundingClientRect().height

      if (height) {
        this.bottomSpace.set(height)
      }

      viewport.checkViewportSize()
      this.restorePosition()
      this.requestNextPage()
    })
    this.observer.observe(viewport.elementRef.nativeElement)
    this.observer.observe(this.clearance()!.nativeElement)
    viewport.renderedRangeStream.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.closeOwnedRows())
    viewport.scrollable
      .elementScrolled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const offset = viewport.scrollable.measureScrollOffset('top')

        if (this.restoringOffset !== undefined && Math.abs(offset - this.restoringOffset) < 1) {
          this.restoringOffset = undefined

          return
        }

        this.restoringOffset = undefined
        this.pendingOffset = undefined

        if (Math.abs(offset - this.lastSwipeOffset) > 50) {
          this.closeOwnedRows()
          this.lastSwipeOffset = offset
        }

        this.savePosition()
        this.requestNextPage()
      })
  }

  /** Keeps the virtual search row aligned with the header fade inset. */
  private syncHeaderInset(): void {
    const viewport = this.viewport()!
    const inset = getComputedStyle(viewport.elementRef.nativeElement).getPropertyValue('--inline-search-top-inset')

    this.headerInset.set(parseFloat(inset) || 0)
  }

  /**
   * Saves the scroll position and closes open swipe trays.
   */
  ngOnDestroy(): void {
    this.savePosition()
    this.observer?.disconnect()
    this.closeOwnedRows()
  }

  /**
   * Scrolls to a content row.
   * @param {number} index
   * @param {ScrollBehavior} [behavior='auto']
   */
  scrollToIndex(index: number, behavior: ScrollBehavior = 'auto'): void {
    this.pendingOffset = undefined
    this.restoringOffset = undefined
    this.viewport()?.scrollToIndex(Math.max(0, index) + this.headerRowCount(), behavior)
    this.savePosition()
  }

  /**
   * Reveals a row when it is outside the visible area above the dock.
   * @param {number} index
   */
  revealRow(index: number): void {
    const viewport = this.viewport()

    if (!viewport || index < 0) {
      return
    }

    const offset = viewport.measureScrollOffset()
    const row = this.rows()[index]

    if (!row) {
      return
    }

    const renderIndex = index + this.headerRowCount()
    const rowOffset = this.itemOffsets()[renderIndex]
    const height = row.height ?? this.rowHeight()
    const visibleHeight = Math.max(0, viewport.getViewportSize() - this.bottomSpace())

    if (rowOffset < offset || rowOffset + height > offset + visibleHeight) {
      const previousOffset = this.itemOffsets()[Math.max(0, renderIndex - 1)]
      // Keep preceding context when it fits, but reveal the target below a tall row.
      const target = Math.min(rowOffset, Math.max(previousOffset, rowOffset + height - visibleHeight))

      this.pendingOffset = undefined
      this.restoringOffset = undefined
      viewport.scrollToOffset(target, 'smooth')
      this.savePosition()
    }
  }

  /**
   * Keeps row identity stable through edits, reordering, and pagination.
   * @param {number} index
   * @param {RenderRow<T>} row
   * @returns {string}
   */
  protected trackRow(index: number, row: RenderRow<T>): string {
    return row.id
  }

  /**
   * Requests an explicit retry without automatic retries.
   */
  protected retry(): void {
    this.pageRequested = true
    this.retryLoadMore.emit()
  }

  /**
   * Restores the saved position as missing pages arrive.
   */
  private restorePosition(): void {
    const viewport = this.viewport()

    if (!viewport || this.pendingOffset === undefined || (this.loading() && !this.rows().length)) {
      return
    }

    const offsets = this.itemOffsets()
    const maximum = Math.max(
      0,
      viewport.measureViewportOffset() + offsets[offsets.length - 1] - viewport.getViewportSize()
    )
    const target = Math.min(this.pendingOffset, maximum)

    if (Math.abs(viewport.scrollable.measureScrollOffset('top') - target) > 1) {
      this.restoringOffset = target
      viewport.scrollToOffset(target)
    }

    if (!this.loadingMore() && (this.pendingOffset <= maximum || !this.hasMore())) {
      this.pendingOffset = undefined
    }
  }

  /**
   * Saves the position after restoration has finished.
   */
  private savePosition(): void {
    if (this.activeKey && this.viewport() && this.pendingOffset === undefined) {
      this.persistence.updateListPosition(this.activeKey, {
        context: this.activeContext,
        offset: this.viewport()!.scrollable.measureScrollOffset('top')
      })
    }
  }

  /**
   * Requests the next page when scrolling nears the content end.
   */
  private requestNextPage(): void {
    const viewport = this.viewport()

    if (
      !viewport ||
      !this.hasMore() ||
      !this.pagingEnabled() ||
      this.loading() ||
      this.loadingMore() ||
      this.loadMoreFailed() ||
      this.pageRequested
    ) {
      return
    }

    const contentEnd = this.itemOffsets()[this.rows().length + this.headerRowCount()]

    if (viewport.measureScrollOffset() + viewport.getViewportSize() >= contentEnd - 280) {
      this.pageRequested = true
      this.requestedRowCount = this.rows().length
      this.loadMore.emit()
    }
  }

  /**
   * Closes swipe trays owned by this list.
   */
  private closeOwnedRows(): void {
    const viewport = this.viewport()

    if (viewport) {
      this.swipes.closeRowsWithin(viewport.elementRef.nativeElement)
    }
  }
}
